import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Divider,
  Stack,
  InputAdornment,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Person as PersonIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  CheckCircle as CheckCircleIcon,
  DeleteForever as DeleteForeverIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from './LanguageSelector';
import LinkedAccountsSection from './LinkedAccountsSection';
import type { AuthUser } from '../../types/common';

interface AccountSettingsTabProps {
  userForm: {
    email: string;
    full_name: string;
    company: string;
    current_password: string;
    new_password: string;
    confirm_password: string;
  };
  loading: boolean;
  currentLanguage: string;
  user: AuthUser | null;
  onFormChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onProfileUpdate: () => void;
  onPasswordChange: () => void;
  onLanguageChange: (language: string) => void;
  onRequestDeletion?: () => void;
  onCancelDeletion?: () => void;
  deletionLoading?: boolean;
}

export const AccountSettingsTab: React.FC<AccountSettingsTabProps> = ({
  userForm,
  loading,
  currentLanguage,
  user,
  onFormChange,
  onProfileUpdate,
  onPasswordChange,
  onLanguageChange,
  onRequestDeletion,
  onCancelDeletion,
  deletionLoading = false,
}) => {
  const { t } = useTranslation();

  // Password visibility state
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Delete account dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cancelDeletionDialogOpen, setCancelDeletionDialogOpen] = useState(false);

  // Helper function to calculate remaining days
  const calculateRemainingDays = (deletedAt: string | null | undefined): number => {
    if (!deletedAt) {return 0;}
    const deletionDate = new Date(deletedAt);
    const expiryDate = new Date(deletionDate.getTime() + 7 * 24 * 60 * 60 * 1000);
    const now = new Date();
    const diffMs = expiryDate.getTime() - now.getTime();
    return Math.ceil(diffMs / (24 * 60 * 60 * 1000));
  };

  // Password visibility toggle handlers
  const handleToggleCurrentPassword = () => setShowCurrentPassword(!showCurrentPassword);
  const handleToggleNewPassword = () => setShowNewPassword(!showNewPassword);
  const handleToggleConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

  // Delete account handlers
  const handleRequestDeletion = () => {
    setDeleteDialogOpen(false);
    if (onRequestDeletion) {
      onRequestDeletion();
    }
  };

  const handleCancelDeletion = () => {
    setCancelDeletionDialogOpen(false);
    if (onCancelDeletion) {
      onCancelDeletion();
    }
  };

  // Password validation logic
  const passwordsMatch = useMemo(() => {
    // Only validate if both fields have content
    if (!userForm.new_password || !userForm.confirm_password) {
      return null; // No validation yet
    }
    return userForm.new_password === userForm.confirm_password;
  }, [userForm.new_password, userForm.confirm_password]);

  const isPasswordValid = useMemo(() => {
    return userForm.new_password.length >= 8;
  }, [userForm.new_password]);

  // Button should be disabled if:
  // - Any field is empty
  // - Passwords don't match
  // - New password is too short
  // - Form is currently loading
  const isPasswordFormInvalid = useMemo(() => {
    return (
      !userForm.current_password ||
      !userForm.new_password ||
      !userForm.confirm_password ||
      passwordsMatch === false ||
      !isPasswordValid
    );
  }, [userForm.current_password, userForm.new_password, userForm.confirm_password, passwordsMatch, isPasswordValid]);

  return (
    <Stack spacing={3}>
      {/* Profile Information Section */}
      <Paper
        elevation={1}
        sx={{
          p: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <PersonIcon sx={{ mr: 1.5, color: 'primary.main' }} />
          <Typography variant="h6">
            {t('settings.account.profileInformation')}
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('settings.account.email')}
              name="email"
              value={userForm.email}
              onChange={onFormChange}
              disabled={true}
              variant="outlined"
              helperText={t('settings.account.emailCannotBeChanged', 'Email cannot be changed')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('settings.account.fullName')}
              name="full_name"
              value={userForm.full_name}
              onChange={onFormChange}
              variant="outlined"
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={onProfileUpdate}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {loading ? t('common.saving', 'Saving...') : t('settings.account.updateProfile')}
          </Button>
        </Box>
      </Paper>

      {/* Security Section */}
      <Paper
        elevation={1}
        sx={{
          p: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <LockIcon sx={{ mr: 1.5, color: 'primary.main' }} />
          <Typography variant="h6">
            {t('settings.account.changePassword')}
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              fullWidth
              label={t('settings.account.currentPassword')}
              name="current_password"
              type={showCurrentPassword ? 'text' : 'password'}
              value={userForm.current_password}
              onChange={onFormChange}
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={t('settings.account.togglePasswordVisibility', 'toggle password visibility')}
                      onClick={handleToggleCurrentPassword}
                      edge="end"
                    >
                      {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('settings.account.newPassword')}
              name="new_password"
              type={showNewPassword ? 'text' : 'password'}
              value={userForm.new_password}
              onChange={onFormChange}
              variant="outlined"
              helperText={t('settings.account.passwordMinLengthHelper', 'Minimum 8 characters')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={t('settings.account.togglePasswordVisibility', 'toggle password visibility')}
                      onClick={handleToggleNewPassword}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('settings.account.confirmNewPassword')}
              name="confirm_password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={userForm.confirm_password}
              onChange={onFormChange}
              variant="outlined"
              error={passwordsMatch === false}
              helperText={
                passwordsMatch === false
                  ? t('settings.messages.passwordsDoNotMatch')
                  : passwordsMatch === true
                  ? t('settings.messages.passwordsMatch')
                  : ''
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {passwordsMatch === true && (
                      <CheckCircleIcon
                        sx={{ color: 'success.main', mr: 1 }}
                        aria-label={t('settings.messages.passwordsMatch')}
                      />
                    )}
                    <IconButton
                      aria-label={t('settings.account.togglePasswordVisibility', 'toggle password visibility')}
                      onClick={handleToggleConfirmPassword}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              FormHelperTextProps={{
                sx: {
                  color: passwordsMatch === true ? 'success.main' : undefined,
                },
              }}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={onPasswordChange}
            disabled={loading || isPasswordFormInvalid}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {loading ? t('common.saving', 'Saving...') : t('settings.account.changePassword')}
          </Button>
        </Box>
      </Paper>

      {/* Language Preferences Section */}
      <LanguageSelector
        currentLanguage={currentLanguage}
        onLanguageChange={onLanguageChange}
        loading={loading}
      />

      {/* Linked Accounts Section */}
      <LinkedAccountsSection />

      {/* Delete Account Section */}
      <Paper
        elevation={1}
        sx={{
          p: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'error.main',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <DeleteForeverIcon sx={{ mr: 1.5, color: 'error.main' }} />
          <Typography variant="h6" color="error">
            {t('users.accountDeletion')}
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {user?.deleted_at && calculateRemainingDays(user.deleted_at) > 0 ? (
          // Account deletion is scheduled
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>{t('users.accountWillBeDeleted')}</strong>
              </Typography>
              <Typography variant="body2">
                {t('users.daysRemaining', { count: calculateRemainingDays(user.deleted_at) })}
              </Typography>
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('users.gracePeriodExplanation')}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="success"
                onClick={() => setCancelDeletionDialogOpen(true)}
                disabled={deletionLoading}
                startIcon={deletionLoading ? <CircularProgress size={20} color="inherit" /> : <CancelIcon />}
              >
                {t('users.cancelDeletion')}
              </Button>
            </Box>
          </>
        ) : (
          // No deletion scheduled
          <>
            <Alert severity="error" sx={{ mb: 2 }}>
              {t('users.deleteAccountWarning')}
            </Alert>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                color="error"
                onClick={() => setDeleteDialogOpen(true)}
                startIcon={<DeleteForeverIcon />}
              >
                {t('users.deleteAccount')}
              </Button>
            </Box>
          </>
        )}
      </Paper>

      {/* Delete Account Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{
          backgroundColor: '#E19E19',
          color: 'white',
          fontFamily: '"Roboto", "Segoe UI", "Helvetica Neue", Arial, sans-serif'
        }}>
          {t('users.confirmDeleteAccountTitle')}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <DialogContentText>
            {t('users.confirmDeleteAccount')}
          </DialogContentText>
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('users.gracePeriodExplanation')}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(102, 102, 102, 0.1)',
              },
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleRequestDeletion}
            variant="contained"
            color="error"
            disabled={deletionLoading}
            sx={{
              backgroundColor: '#E19E19',
              '&:hover': {
                backgroundColor: '#c38400',
              },
            }}
          >
            {deletionLoading ? <CircularProgress size={24} /> : t('users.deleteAccount')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Deletion Confirmation Dialog */}
      <Dialog open={cancelDeletionDialogOpen} onClose={() => setCancelDeletionDialogOpen(false)}>
        <DialogTitle sx={{
          backgroundColor: '#003064',
          color: 'white',
          fontFamily: '"Roboto", "Segoe UI", "Helvetica Neue", Arial, sans-serif'
        }}>
          {t('users.cancelDeletionConfirm')}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <DialogContentText>
            {t('users.cancelDeletionMessage')}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setCancelDeletionDialogOpen(false)}
            sx={{
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(102, 102, 102, 0.1)',
              },
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleCancelDeletion}
            variant="contained"
            disabled={deletionLoading}
            sx={{
              backgroundColor: '#003064',
              '&:hover': {
                backgroundColor: '#002550',
              },
            }}
          >
            {deletionLoading ? <CircularProgress size={24} /> : t('users.cancelDeletion')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
